/*
 * <contact-form-tabs> — request-type tabs for the contact page.
 *
 * The "Objet de la demande" select no longer submits anything: each request
 * type has its own form in the Shopify Forms app (that is what lets a Flow
 * workflow set the notification email's subject per type), and this element
 * decides which of those forms is on screen.
 *
 * Three outcomes per selection:
 *  - the type redirects  -> navigate to its dedicated page's form
 *  - the type has a form -> reveal that form, hide the others
 *  - neither             -> keep the hint visible, reveal nothing
 *
 * Everything is keyed by the option's data-index (its position in
 * `subject_options`), never by its label — the labels are translatable.
 *
 * Showing a form is a class toggle (`.cform__tab--active`), not the `hidden`
 * attribute: the Forms widget mispositions its floating labels for good if it
 * mounts inside a display:none box. See the .cform__tabs block in custom.css.
 */
class ContactFormTabs extends HTMLElement {
  connectedCallback() {
    try {
      this.redirects = JSON.parse(this.dataset.redirects || '{}');
    } catch (e) {
      this.redirects = {};
    }

    this.select = this.querySelector('[data-cform-subject]');
    this.tabs = Array.from(this.querySelectorAll('[data-cform-tab]'));
    this.hint = this.querySelector('[data-cform-tab-hint]');
    if (!this.select) return;

    this.select.addEventListener('change', this.#onChange);
    // A browser-restored selection should render its form, not an empty panel.
    this.#apply();
  }

  disconnectedCallback() {
    this.select?.removeEventListener('change', this.#onChange);
  }

  #onChange = () => {
    const option = this.select.selectedOptions[0];
    // A redirect leaves the page, so there is no point revealing anything.
    if (this.#redirect(option)) return;
    this.#apply();
  };

  #apply() {
    const option = this.select.selectedOptions[0];
    const index = option ? option.dataset.index : null;
    let shown = false;

    this.tabs.forEach((tab) => {
      const match = index != null && tab.dataset.cformTab === index;
      tab.classList.toggle('cform__tab--active', match);
      if (match) shown = true;
    });

    // The hint stays up until a type with a form is picked — including for a
    // type whose form hasn't been created in the app yet, so the panel is never
    // silently blank.
    if (this.hint) this.hint.hidden = shown;
  }

  // Only same-origin paths are honoured, so a bad setting can't send visitors
  // off-site. Navigates in the current tab (window.open with 'noopener'
  // returns null even on success, which makes a fallback fire twice).
  #redirect(option) {
    const url = option && this.redirects[option.dataset.index];
    if (!url) return false;
    let target;
    try {
      target = new URL(url, window.location.origin);
    } catch (e) {
      return false;
    }
    if (target.origin !== window.location.origin) return false;
    window.location.assign(target.href);
    return true;
  }
}

if (!customElements.get('contact-form-tabs')) {
  customElements.define('contact-form-tabs', ContactFormTabs);
}
